(function() {
  'use strict';

  // ============ Constants ============
  
  const STORAGE_PREFIX = 'loadMore_';
  const CACHE_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes
  const IDLE_RESTORE_TIMEOUT = 100;
  const FALLBACK_RESTORE_TIMEOUT = 50;

  // ============ Utilities ============

  // Storage keys factory
  const getStorageKeys = (collectionUrl) => {
    const handleized = collectionUrl.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    return {
      state: `${STORAGE_PREFIX}state_${handleized}`,
      html: `${STORAGE_PREFIX}html_${handleized}`
    };
  };

  // Storage utilities with error handling
  const Storage = {
    get(key) {
      try {
        const item = sessionStorage.getItem(key);
        return item ? JSON.parse(item) : null;
      } catch (e) {
        console.warn(`Failed to get ${key}:`, e);
        return null;
      }
    },

    set(key, value) {
      try {
        sessionStorage.setItem(key, JSON.stringify(value));
        return true;
      } catch (e) {
        console.warn(`Failed to set ${key}:`, e);
        if (e.name === 'QuotaExceededError') {
          this.remove(key);
        }
        return false;
      }
    },

    remove(key) {
      try {
        sessionStorage.removeItem(key);
      } catch (e) {
        console.warn(`Failed to remove ${key}:`, e);
      }
    }
  };

  // URL utilities
  const UrlHelper = {
    getPageNumber() {
      const params = new URLSearchParams(window.location.search);
      return parseInt(params.get('page'), 10) || 1;
    },

    updatePageNumber(page) {
      const url = new URL(window.location.href);
      if (page > 1) {
        url.searchParams.set('page', page);
      } else {
        url.searchParams.delete('page');
      }
      history.replaceState({ loadMorePage: page }, '', url.toString());
    },

    buildCollectionUrl(baseUrl, page) {
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set('page', page);

      // Preserve existing query parameters (filters, sort, etc.)
      const currentParams = new URLSearchParams(window.location.search);
      for (const key of new Set(currentParams.keys())) {
        if (key === 'page') continue;
        url.searchParams.delete(key);
        for (const value of currentParams.getAll(key)) url.searchParams.append(key, value);
      }

      return url.toString();
    },

    getSearchParamsWithoutPage() {
      const params = new URLSearchParams(window.location.search);
      params.delete('page');
      return params.toString();
    }
  };

  // Cache validation
  const CacheValidator = {
    isValid(cacheData) {
      if (!cacheData || !cacheData.timestamp) return false;

      const isExpired = Date.now() - cacheData.timestamp > CACHE_EXPIRY_MS;
      const isSamePage = cacheData.pathname === window.location.pathname;
      const isSameFilters = this.compareFilters(cacheData.search);

      return !isExpired && isSamePage && isSameFilters;
    },

    compareFilters(cachedSearch) {
      const currentFilters = UrlHelper.getSearchParamsWithoutPage();
      const cachedParams = new URLSearchParams(cachedSearch);
      cachedParams.delete('page');
      return currentFilters === cachedParams.toString();
    }
  };

  // ============ Main Class ============

  class LoadMore {
    constructor(container) {
      this.container = container;
      this.button = container.querySelector('[data-load-more-button]');
      this.productGrid = document.getElementById('product-grid');
      
      // Configuration from data attributes
      this.config = {
        collectionUrl: container.dataset.collectionUrl,
        currentPage: parseInt(container.dataset.currentPage, 10),
        totalPages: parseInt(container.dataset.totalPages, 10),
        sectionId: container.dataset.sectionId,
        isSearch: container.dataset.isSearch === 'true',
        searchTerms: container.dataset.searchTerms || ''
      };

      // State management
      this.state = {
        isLoading: false,
        isRestoring: false,
        abortController: null
      };

      // Storage keys - include search terms for search pages
      const storageKey = this.config.isSearch 
        ? `${this.config.collectionUrl}_${this.config.searchTerms}`
        : this.config.collectionUrl;
      this.storageKeys = getStorageKeys(storageKey);

      this.onLoadMore = this.handleLoadMore.bind(this);
      this.onProductClick = this.handleProductClick.bind(this);
      this.onPageShow = this.handlePageShow.bind(this);
      this.onPageHide = this.handlePageHide.bind(this);
      this.init();
    }

    // Check if #product-grid is the <ul> itself (collection) or a <div> wrapper (search)
    isProductGridTheUl() {
      return this.productGrid && this.productGrid.tagName === 'UL';
    }

    // Get the <ul> element to append items to
    getItemsContainer() {
      if (this.isProductGridTheUl()) {
        // Collection: #product-grid IS the <ul>
        return this.productGrid;
      }
      // Search: <ul> is nested inside #product-grid
      return this.productGrid.querySelector('ul.product-grid');
    }

    init() {
      this.bindEventListeners();
      this.checkRestoreState();
    }

    bindEventListeners() {
      if (this.button) {
        this.button.addEventListener('click', this.onLoadMore);
      }
      
      if (this.productGrid) {
        this.productGrid.addEventListener('click', this.onProductClick);
      }
      
      window.addEventListener('pageshow', this.onPageShow);
      window.addEventListener('pagehide', this.onPageHide);
    }

    // ============ State Management ============

    checkRestoreState() {
      const urlPage = UrlHelper.getPageNumber();
      
      if (urlPage > 1) {
        // Try instant restore from HTML cache
        if (!this.restoreFromHtmlCache()) {
          // Fallback to fetching pages
          this.loadPagesUpTo(urlPage, 0, false);
        }
      } else {
        // Schedule restore for later to avoid blocking initial render
        this.scheduleRestore();
      }
    }

    scheduleRestore() {
      const callback = () => this.restoreState();
      
      if ('requestIdleCallback' in window) {
        this.restoreIdleId = requestIdleCallback(callback, { timeout: IDLE_RESTORE_TIMEOUT });
      } else {
        this.restoreTimerId = setTimeout(callback, FALLBACK_RESTORE_TIMEOUT);
      }
    }

    saveState() {
      const state = {
        currentPage: this.config.currentPage,
        scrollPosition: window.scrollY,
        timestamp: Date.now(),
        pathname: window.location.pathname,
        search: window.location.search
      };
      
      Storage.set(this.storageKeys.state, state);
    }

    restoreState() {
      const savedState = Storage.get(this.storageKeys.state);
      if (!savedState) return;

      if (!CacheValidator.isValid(savedState)) {
        this.clearCache();
        return;
      }

      // Try HTML cache first for instant restore
      if (savedState.currentPage > 1) {
        if (!this.restoreFromHtmlCache()) {
          this.loadPagesUpTo(savedState.currentPage, savedState.scrollPosition);
        }
      }
    }

    clearCache() {
      Storage.remove(this.storageKeys.state);
      Storage.remove(this.storageKeys.html);
    }

    // ============ HTML Cache Management ============

    saveHtmlCache() {
      if (!this.productGrid || this.config.currentPage <= 1) return;

      // Cache only the items container (the <ul>), not the entire #product-grid
      // This prevents caching the load-more button on search pages
      const itemsContainer = this.getItemsContainer();
      if (!itemsContainer) return;

      const cacheData = {
        html: itemsContainer.innerHTML,
        currentPage: this.config.currentPage,
        totalPages: this.config.totalPages,
        buttonHidden: this.config.currentPage >= this.config.totalPages,
        timestamp: Date.now(),
        pathname: window.location.pathname,
        search: window.location.search
      };

      Storage.set(this.storageKeys.html, cacheData);
    }

    restoreFromHtmlCache() {
      const cacheData = Storage.get(this.storageKeys.html);
      if (!cacheData) return false;

      if (!CacheValidator.isValid(cacheData)) {
        Storage.remove(this.storageKeys.html);
        return false;
      }

      // Restore HTML to the items container (the <ul>)
      const itemsContainer = this.getItemsContainer();
      if (!itemsContainer) return false;

      itemsContainer.innerHTML = cacheData.html;
      this.updateCurrentPage(cacheData.currentPage);

      // Update button state
      if (this.button && cacheData.buttonHidden) {
        this.button.style.display = 'none';
      }

      // Restore scroll position
      this.restoreScrollPosition();

      return true;
    }

    restoreScrollPosition() {
      const savedState = Storage.get(this.storageKeys.state);
      if (!savedState || savedState.scrollPosition <= 0) return;

      requestAnimationFrame(() => {
        window.scrollTo({
          top: savedState.scrollPosition,
          behavior: 'instant'
        });
      });
    }

    // ============ Load More Functionality ============

    async handleLoadMore() {
      if (this.cannotLoadMore()) return;

      this.state.isLoading = true;
      this.setLoadingState(true);

      // Create abort controller for this request
      this.state.abortController = new AbortController();

      try {
        const nextPage = this.config.currentPage + 1;
        const products = await this.fetchProducts(nextPage, this.state.abortController.signal);

        if (products.length > 0) {
          this.appendProducts(products);
          this.updateCurrentPage(nextPage);
          this.updateUrlAndCache();
          this.updateButtonVisibility();
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted');
        } else {
          console.error('Load more error:', error);
        }
      } finally {
        this.state.isLoading = false;
        this.state.abortController = null;
        this.setLoadingState(false);
      }
    }

    cannotLoadMore() {
      return this.state.isLoading || 
             this.state.isRestoring || 
             this.config.currentPage >= this.config.totalPages;
    }

    async fetchProducts(page, signal = null) {
      const url = this.buildFetchUrl(page);
      const options = signal ? { signal } : {};
      const response = await fetch(url, options);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch products: ${response.status}`);
      }

      const html = await response.text();
      return this.parseProducts(html);
    }

    buildFetchUrl(page) {
      const url = new URL(this.config.collectionUrl, window.location.origin);
      url.searchParams.set('page', page);

      // For search pages, include section_id and search query
      if (this.config.isSearch) {
        url.searchParams.set('section_id', this.config.sectionId);
        if (this.config.searchTerms) {
          url.searchParams.set('q', this.config.searchTerms);
        }
        url.searchParams.set('options[prefix]', 'last');
      }

      // Preserve existing query parameters (filters, sort, etc.)
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.forEach((value, key) => {
        if (key !== 'page' && key !== 'section_id' && !(this.config.isSearch && key === 'q')) {
          url.searchParams.set(key, value);
        }
      });

      return url.toString();
    }

    parseProducts(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const productGrid = doc.getElementById('product-grid');
      if (!productGrid) return [];

      // Check if #product-grid is the <ul> (collection) or a wrapper (search)
      const isGridTheUl = productGrid.tagName === 'UL';
      
      if (isGridTheUl) {
        // Collection: items are direct children of #product-grid
        return Array.from(productGrid.querySelectorAll(':scope > .grid__item'));
      } else {
        // Search: items are inside <ul class="product-grid">
        const ul = productGrid.querySelector('ul.product-grid');
        return ul ? Array.from(ul.querySelectorAll(':scope > .grid__item')) : [];
      }
    }

    appendProducts(products) {
      const itemsContainer = this.getItemsContainer();
      if (!itemsContainer) return;

      const existingCount = itemsContainer.querySelectorAll('.grid__item').length;

      products.forEach((product, index) => {
        product.style.setProperty('--animation-order', existingCount + index + 1);
        itemsContainer.appendChild(product.cloneNode(true));
      });
    }

    async loadPagesUpTo(targetPage, scrollPosition = 0, shouldScroll = true) {
      if (!this.button) return;

      // Prevent multiple simultaneous restores
      if (this.state.isRestoring) {
        console.warn('Restore already in progress');
        return;
      }

      this.state.isRestoring = true;
      this.setLoadingState(true);

      // Create abort controller for restoration
      this.state.abortController = new AbortController();

      const itemsContainer = this.getItemsContainer();
      if (!itemsContainer) {
        this.state.isRestoring = false;
        this.setLoadingState(false);
        return;
      }

      try {
        for (let page = 2; page <= targetPage; page++) {
          const products = await this.fetchProducts(page, this.state.abortController.signal);
          
          // Remove animations for restored items
          products.forEach((product) => {
            this.removeAnimationClasses(product);
            itemsContainer.appendChild(product.cloneNode(true));
          });

          this.updateCurrentPage(page);
        }

        this.updateUrlAndCache();
        this.updateButtonVisibility();

        if (shouldScroll && scrollPosition > 0) {
          this.scrollToPosition(scrollPosition);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('Restore aborted');
        } else {
          console.error('Error loading pages:', error);
        }
      } finally {
        this.state.isRestoring = false;
        this.state.abortController = null;
        this.setLoadingState(false);
      }
    }

    removeAnimationClasses(element) {
      element.classList.remove('scroll-trigger', 'animate--slide-in');
      element.removeAttribute('data-cascade');
      element.style.removeProperty('--animation-order');
    }

    scrollToPosition(position) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo({
            top: position,
            behavior: 'instant'
          });
        });
      });
    }

    // ============ UI Updates ============

    updateCurrentPage(page) {
      this.config.currentPage = page;
      this.container.dataset.currentPage = page;
    }

    updateUrlAndCache() {
      UrlHelper.updatePageNumber(this.config.currentPage);
      this.saveState();
      this.saveHtmlCache();
    }

    updateButtonVisibility() {
      if (this.button && this.config.currentPage >= this.config.totalPages) {
        this.button.style.display = 'none';
      }
    }

    setLoadingState(loading) {
      if (!this.button) return;
      
      this.button.classList.toggle('is-loading', loading);
      this.button.disabled = loading;
    }

    // ============ Event Handlers ============

    handleProductClick(e) {
      const link = e.target.closest('a[href*="/products/"]');
      if (!link || this.config.currentPage <= 1) return;

      // Save state before navigation for proper back button behavior
      this.saveState();
      this.saveHtmlCache();
    }

    async handlePageShow(e) {
      // Page restored from bfcache - DOM is intact, nothing needed
      if (e.persisted) return;

      // Fresh navigation - check if restoration is needed
      const urlPage = UrlHelper.getPageNumber();
      const currentDisplayedPage = this.config.currentPage;

      // If URL page doesn't match what's currently displayed, we need to restore/reset
      if (urlPage !== currentDisplayedPage) {
        if (urlPage === 1) {
          // Going back to page 1 - fetch and replace with page 1 only
          await this.resetToPageOne();
        } else if (urlPage > 1) {
          // Going to a specific page (forward or back)
          if (!this.restoreFromHtmlCache()) {
            await this.loadPagesUpTo(urlPage, 0, false);
          }
        }
      }
    }

    async resetToPageOne() {
      if (!this.productGrid || this.state.isRestoring) return;

      this.state.isRestoring = true;
      this.setLoadingState(true);

      const itemsContainer = this.getItemsContainer();
      if (!itemsContainer) {
        this.state.isRestoring = false;
        this.setLoadingState(false);
        return;
      }

      try {
        // Fetch page 1
        const products = await this.fetchProducts(1);
        
        if (products.length > 0) {
          // Clear the items container
          itemsContainer.innerHTML = '';
          
          // Add page 1 products
          products.forEach((product, index) => {
            product.style.setProperty('--animation-order', index + 1);
            itemsContainer.appendChild(product.cloneNode(true));
          });

          // Reset state
          this.updateCurrentPage(1);
          
          // Show the load more button again
          if (this.button && this.config.totalPages > 1) {
            this.button.style.display = '';
          }

          // Clear cache since we're back to page 1
          this.clearCache();
          
          // Scroll to top
          window.scrollTo({
            top: 0,
            behavior: 'instant'
          });
        }
      } catch (error) {
        console.error('Error resetting to page 1:', error);
        // Fallback to page reload if fetch fails
        window.location.reload();
      } finally {
        this.state.isRestoring = false;
        this.setLoadingState(false);
      }
    }

    handlePageHide() {
      if (this.config.currentPage > 1) {
        this.saveState();
        this.saveHtmlCache();
      }

      // Abort any in-flight requests
      if (this.state.abortController) {
        this.state.abortController.abort();
      }
    }

    // ============ Cleanup ============

    destroy() {
      if (this.restoreIdleId !== undefined) cancelIdleCallback(this.restoreIdleId);
      clearTimeout(this.restoreTimerId);
      // Abort any in-flight requests
      if (this.state.abortController) {
        this.state.abortController.abort();
      }

      // Remove event listeners
      if (this.button) {
        this.button.removeEventListener('click', this.onLoadMore);
      }
      
      if (this.productGrid) {
        this.productGrid.removeEventListener('click', this.onProductClick);
      }
      
      window.removeEventListener('pageshow', this.onPageShow);
      window.removeEventListener('pagehide', this.onPageHide);
    }
  }

  // ============ Module Initialization ============

  let currentInstance = null;

  function init() {
    // Clean up previous instance
    if (currentInstance) {
      currentInstance.destroy();
      currentInstance = null;
    }

    const container = document.querySelector('[data-load-more]');
    if (container) {
      currentInstance = new LoadMore(container);
    }
  }

  // Expose init function globally for facet filtering
  window.initLoadMore = init;

  // Auto-initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();